'use client';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';
import { useIsMobile } from '@/hooks/useIsMobile';

import PackageCardRow from './PackageCardRow';
import type { PackageCardContent, PackageCardsSectionProps } from './types';

export type { PackageCardContent, PackageCardsSectionProps } from './types';
export type { PackageCardPresentation } from '@/types/packageCard';

const DEFAULT_SECTION_CLASSES =
  'content-wrapper no-scroll-reveal relative mx-auto';

function chunk<T>(items: readonly T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

export default function PackageCardsSection<
  T extends PackageCardContent = PackageCardContent,
>({
  cards,
  labels,
  heading,
  description,
  columns = 3,
  footerCta,
  presentation,
  onEnquire,
  onBook,
  canBook,
  className,
}: PackageCardsSectionProps<T>) {
  const isMobile = useIsMobile();
  const rows = chunk(
    cards.map((card, index) => ({ card, index })),
    isMobile ? 1 : columns,
  );

  return (
    <section
      className={`${DEFAULT_SECTION_CLASSES}${className ? ` ${className}` : ''}`}
    >
      {heading?.content && (
        <RichText styles={heading.styles}>{heading.content}</RichText>
      )}
      {description?.content && (
        <RichText styles={description.styles}>{description.content}</RichText>
      )}

      <div className="flex flex-col gap-6">
        {rows.map((row) => (
          <PackageCardRow
            key={row[0]?.card.id ?? row[0]?.index ?? 0}
            cards={row}
            columns={columns}
            labels={labels}
            presentation={presentation}
            onEnquire={onEnquire}
            onBook={onBook}
            canBook={canBook}
          />
        ))}
      </div>

      {footerCta && (
        <div className="mt-10 flex justify-center">
          <PrimaryButton data={footerCta} />
        </div>
      )}
    </section>
  );
}
